import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSignIn } from '@clerk/clerk-react'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Eye, EyeOff } from 'lucide-react'
import logo from '../../assets/logo/logo.png'
import { showConsentSettings } from '../../utils/cookieConsentConfig'
import logger from '../../utils/logger'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Second factor verification state
  const [needsSecondFactor, setNeedsSecondFactor] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')

  const { signIn, isLoaded: clerkLoaded, setActive } = useSignIn()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!clerkLoaded || !signIn) return

    setLoading(true)
    setError('')

    try {
      const result = await signIn.create({
        identifier: email,
        password,
      })

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId })
        navigate('/calendar')
      } else if (result.status === 'needs_second_factor') {
        // Prepare email code second factor
        const secondFactors = result.supportedSecondFactors
        const emailCodeFactor = secondFactors?.find(
          (f: any) => f.strategy === 'email_code'
        )
        const phoneFactor = secondFactors?.find(
          (f: any) => f.strategy === 'phone_code'
        )

        if (emailCodeFactor) {
          await signIn.prepareSecondFactor({ strategy: 'email_code' })
          setNeedsSecondFactor(true)
        } else if (phoneFactor) {
          await signIn.prepareSecondFactor({ strategy: 'phone_code' })
          setNeedsSecondFactor(true)
        } else {
          logger.error('Login: no supported second factor', secondFactors)
          setError('Additional verification required but no supported method available.')
        }
      } else if (result.status === 'needs_first_factor') {
        // Password was not accepted as first factor, or additional first factor needed
        const firstFactors = result.supportedFirstFactors
        const emailCodeFactor = firstFactors?.find(
          (f: any) => f.strategy === 'email_code'
        )

        if (emailCodeFactor) {
          await signIn.prepareFirstFactor({
            strategy: 'email_code',
            emailAddressId: (emailCodeFactor as any).emailAddressId,
          })
          setNeedsSecondFactor(true) // reuse the same UI for code input
        } else {
          logger.error('Login: unhandled first factor status', firstFactors)
          setError('Additional verification required. Try using "Forgot password" to reset your account.')
        }
      } else {
        logger.error('Login: unexpected status', result.status)
        setError('Additional verification required. Try using "Forgot password" to reset your account.')
      }
    } catch (err: any) {
      const msg = err?.errors?.[0]?.longMessage
        || err?.errors?.[0]?.message
        || err?.message
        || 'Login error'
      logger.error('Login error:', msg)
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!clerkLoaded || !signIn) return

    setLoading(true)
    setError('')

    try {
      // Try second factor first, fall back to first factor
      let result
      try {
        result = await signIn.attemptSecondFactor({
          strategy: 'email_code',
          code: verificationCode,
        })
      } catch {
        result = await signIn.attemptFirstFactor({
          strategy: 'email_code',
          code: verificationCode,
        })
      }

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId })
        navigate('/calendar')
      } else {
        setError('Verification failed. Please try again.')
      }
    } catch (err: any) {
      const msg = err?.errors?.[0]?.longMessage
        || err?.errors?.[0]?.message
        || err?.message
        || 'Verification error'
      logger.error('Verification error:', msg)
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo Header */}
        <div className="text-center mb-8">
          <Link to="/" className="relative inline-flex items-center justify-center mb-2" title="Return to home page">
            <img src={logo} alt="2homes.app" className="h-12 w-12 rounded-md shadow-sm" />
          </Link>
          <h1 className="hero-title text-3xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">
            <Link to="/" title="Return to home page">2homes.app</Link>
          </h1>
          <p className="text-slate-600 mt-2">
            Manage your co-parenting schedule
          </p>
        </div>

        {/* Login Form */}
        <div className="card-glass p-8">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-slate-800">
              {needsSecondFactor ? 'Verify your email' : 'Sign in'}
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              {needsSecondFactor
                ? 'Enter the verification code sent to your email'
                : 'Enter your details to continue'}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {needsSecondFactor ? (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-slate-700 mb-1">
                  Verification code
                </label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="Enter code from email"
                  required
                  className="w-full bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-400 focus:ring-opacity-20"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || !clerkLoaded}
                className="w-full neon-button"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setNeedsSecondFactor(false)
                  setVerificationCode('')
                  setError('')
                }}
                className="w-full text-sm text-slate-500 hover:text-slate-700"
              >
                Back to login
              </button>
            </form>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                    Email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="w-full bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-400 focus:ring-opacity-20"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      required
                      className="w-full pr-10 bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-400 focus:ring-opacity-20"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !clerkLoaded}
                  className="w-full neon-button"
                >
                  {loading ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <Link
                  to="/auth/forgot-password"
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Forgot password?
                </Link>
              </div>

              <div className="mt-6 text-center">
                <p className="text-sm text-slate-600">
                  Don't have an account?{' '}
                  <Link
                    to="/auth/register"
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Sign up
                  </Link>
                </p>
              </div>
            </>
          )}

          {/* Privacy Links */}
          <div className="mt-4 text-center text-xs text-gray-400 space-x-4">
            <button
              type="button"
              className="hover:underline bg-transparent border-0 text-xs text-gray-400 cursor-pointer p-0"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                showConsentSettings();
              }}
            >
              Consent preferences
            </button>
            <span>•</span>
            <Link to="/privacy-policy" className="hover:underline">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </div>
  )
}
